(() => {
  'use strict';

  if (customElements.get('tuck-pdp-purchase')) return;

  class TuckPdpPurchase extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.section = document.querySelector(`[data-section-id="${CSS.escape(this.dataset.sectionId)}"][data-pdp-profile]`);
      this.form = document.getElementById(this.dataset.formId);
      this.mainButton = this.form?.querySelector('[data-pdp-add-to-cart]');
      this.mainButtonText = this.mainButton?.querySelector('[data-add-to-cart-text]');
      this.stickyButton = this.querySelector('[data-pdp-sticky-button]');
      this.stickyPrice = this.querySelector('[data-pdp-sticky-price]');
      this.stickySizes = this.querySelector('[data-pdp-sticky-sizes]');
      this.stickySizeButtons = [...this.querySelectorAll('[data-pdp-sticky-size]')];
      this.status = this.querySelector('[data-pdp-sticky-status]');
      this.sizeToast = document.querySelector(
        `[data-pdp-size-toast="${CSS.escape(this.dataset.sectionId)}"]`
      );
      this.requiresSize = this.dataset.requiresSize === 'true';
      this.preorderCta = this.dataset.preorderCta || '';
      this.sizeInputs = this.section ? [...this.section.querySelectorAll('[data-pdp-size-input]')] : [];
      this.messageContainer = this.section?.querySelector('[data-pdp-standard-messages]');
      this.messageGroups = this.messageContainer
        ? [...this.messageContainer.querySelectorAll('[data-pdp-message-group]')]
        : [];
      this.benefitPolicies = this.section
        ? [...this.section.querySelectorAll('[data-pdp-benefit-policy]')]
        : [];
      this.paymentPlan = this.section?.querySelector('[data-coastal-payment-plans]');
      this.paymentPlanAmount = this.paymentPlan?.querySelector('[data-coastal-payment-plan-amount]');
      this.paymentInstallmentCount = Number(this.paymentPlan?.dataset.paymentInstallmentCount || 0);
      this.sizeSelected = !this.requiresSize;
      this.currentVariant = null;
      try {
        this.variants = JSON.parse(this.section?.querySelector('[data-variant-json]')?.value || '[]');
      } catch {
        this.variants = [];
      }

      if (!this.section || !this.form || !this.mainButton || !this.stickyButton) return;

      this.boundClick = this.onStickyClick.bind(this);
      this.boundSizeChange = this.onSizeChange.bind(this);
      this.boundVariantChange = this.onVariantChange.bind(this);
      this.boundOptionChange = () => requestAnimationFrame(() => this.updateState());
      this.boundSubmit = this.onSubmit.bind(this);
      this.boundStickySizeClick = this.onStickySizeClick.bind(this);
      this.stickyButton.addEventListener('click', this.boundClick);
      this.stickySizeButtons.forEach((button) => button.addEventListener('click', this.boundStickySizeClick));
      this.sizeInputs.forEach((input) => input.addEventListener('change', this.boundSizeChange));
      this.section.addEventListener('variantChange', this.boundVariantChange);
      this.section.addEventListener('change', this.boundOptionChange);
      this.form.addEventListener('submit', this.boundSubmit, true);
      this.setupStickyVisibility();

      if (this.requiresSize) {
        this.sizeInputs.forEach((input) => {
          input.checked = false;
        });
      }

      this.updateState();
      requestAnimationFrame(() => this.updateState());
    }

    disconnectedCallback() {
      this.stickyButton?.removeEventListener('click', this.boundClick);
      this.stickySizeButtons?.forEach((button) => button.removeEventListener('click', this.boundStickySizeClick));
      this.sizeInputs?.forEach((input) => input.removeEventListener('change', this.boundSizeChange));
      this.section?.removeEventListener('variantChange', this.boundVariantChange);
      this.section?.removeEventListener('change', this.boundOptionChange);
      this.form?.removeEventListener('submit', this.boundSubmit, true);
      window.removeEventListener('resize', this.boundStickyVisibilityCheck);
      window.removeEventListener('scroll', this.boundStickyVisibilityCheck);
      this.mainButtonObserver?.disconnect();
      window.cancelAnimationFrame(this.stickyVisibilityFrame);
      document.documentElement.classList.remove('has-tuck-pdp-sticky');
      window.clearTimeout(this.toastTimer);
      this.initialized = false;
    }

    setupStickyVisibility() {
      this.mobileViewport = window.matchMedia('(max-width: 768px)');
      this.boundStickyVisibilityCheck = this.scheduleStickyVisibilityCheck.bind(this);
      window.addEventListener('resize', this.boundStickyVisibilityCheck);

      this.mainButtonFullyVisible = this.isMainButtonFullyVisible();
      if ('IntersectionObserver' in window) {
        this.mainButtonObserver = new IntersectionObserver(
          ([entry]) => {
            this.mainButtonFullyVisible = entry.isIntersecting && entry.intersectionRatio >= 0.999;
            this.scheduleStickyVisibilityCheck();
          },
          { threshold: [0, 0.999, 1] }
        );
        this.mainButtonObserver.observe(this.mainButton);
      } else {
        window.addEventListener('scroll', this.boundStickyVisibilityCheck, { passive: true });
      }

      this.scheduleStickyVisibilityCheck();
    }

    isMainButtonFullyVisible() {
      const bounds = this.mainButton.getBoundingClientRect();
      return (
        bounds.top >= 0 &&
        bounds.left >= 0 &&
        bounds.bottom <= window.innerHeight &&
        bounds.right <= window.innerWidth
      );
    }

    scheduleStickyVisibilityCheck() {
      if (this.stickyVisibilityFrame) return;
      this.stickyVisibilityFrame = window.requestAnimationFrame(() => {
        this.stickyVisibilityFrame = null;
        if (!this.mainButtonObserver) {
          this.mainButtonFullyVisible = this.isMainButtonFullyVisible();
        }
        this.setStickyVisibility(this.mobileViewport.matches && !this.mainButtonFullyVisible);
      });
    }

    setStickyVisibility(visible) {
      this.hidden = !visible;
      document.documentElement.classList.toggle('has-tuck-pdp-sticky', visible);
      if (!visible) this.closeSizeChooser();
    }

    onSizeChange(event) {
      this.sizeSelected = Boolean(event.target.checked);
      this.closeSizeChooser();
      requestAnimationFrame(() => this.updateState());
    }

    onVariantChange(event) {
      this.currentVariant = event.detail?.variant || null;
      requestAnimationFrame(() => this.updateState());
    }

    onSubmit(event) {
      if (!this.requiresSize || this.sizeSelected) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.promptForSize();
    }

    onStickyClick() {
      if (this.requiresSize && !this.sizeSelected) {
        this.toggleSizeChooser();
        return;
      }
      if (!this.currentVariant && this.requiresSize) return;
      if (this.currentVariant && !this.currentVariant.available) return;
      const quantity = this.form.querySelector('[name="quantity"]');
      if (quantity) quantity.value = '1';
      this.form.requestSubmit(this.mainButton);
    }

    onStickySizeClick(event) {
      const value = event.currentTarget.dataset.pdpStickySize;
      const input = this.sizeInputs.find((candidate) => candidate.value === value);
      if (!input || input.disabled) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    toggleSizeChooser() {
      if (!this.stickySizes) {
        this.promptForSize();
        return;
      }
      const opening = this.stickySizes.hidden;
      this.stickySizes.hidden = !opening;
      this.stickyButton.setAttribute('aria-expanded', String(opening));
      if (opening) {
        const firstAvailable = this.stickySizeButtons.find((button) => !button.disabled);
        requestAnimationFrame(() => firstAvailable?.focus({ preventScroll: true }));
      }
    }

    closeSizeChooser() {
      if (!this.stickySizes) return;
      this.stickySizes.hidden = true;
      this.stickyButton.setAttribute('aria-expanded', 'false');
    }

    promptForSize() {
      const wrapper = this.sizeInputs[0]?.closest('.variant-wrapper');
      if (!wrapper) return;
      this.showSizeToast();
      const firstAvailable = this.sizeInputs.find((input) => !input.disabled);
      requestAnimationFrame(() => firstAvailable?.focus({ preventScroll: true }));
      this.status.textContent = 'Select an available size before adding this item.';
    }

    showSizeToast() {
      if (!this.sizeToast) return;
      window.clearTimeout(this.toastTimer);
      this.sizeToast.hidden = false;
      requestAnimationFrame(() => this.sizeToast.classList.add('is-visible'));
      this.toastTimer = window.setTimeout(() => {
        this.sizeToast.classList.remove('is-visible');
        window.setTimeout(() => {
          this.sizeToast.hidden = true;
        }, this.reducedMotion() ? 0 : 200);
      }, 2500);
    }

    updateSizeAvailability() {
      if (!this.variants?.length || !this.sizeInputs.length) return;
      const sizeIndex = Number(this.sizeInputs[0].dataset.index?.replace('option', '')) - 1;
      if (!Number.isInteger(sizeIndex) || sizeIndex < 0) return;
      const selections = new Map();
      this.section.querySelectorAll('[data-variant-input][data-index]').forEach((input) => {
        const index = Number(input.dataset.index.replace('option', '')) - 1;
        if (index === sizeIndex || !Number.isInteger(index) || index < 0) return;
        if (input.tagName === 'SELECT' || input.checked) {
          selections.set(index, input.value);
        }
      });
      this.sizeInputs.forEach((input) => {
        const available = this.variants.some((variant) => {
          const optionValue = (index) => variant.options?.[index] ?? variant['option' + (index + 1)];
          return variant.available &&
            optionValue(sizeIndex) === input.value &&
            [...selections].every(([index, value]) => optionValue(index) === value);
        });
        input.disabled = !available;
        input.classList.toggle('disabled', !available);
        [...(input.labels || [])].forEach((label) => label.classList.toggle('disabled', !available));
        if (!available && input.checked) {
          input.checked = false;
          this.sizeSelected = false;
          this.currentVariant = null;
        }
      });
    }

    updateState() {
      this.updateSizeAvailability();
      const productUnavailable = this.dataset.productAvailable !== 'true';
      const variantUnavailable = this.currentVariant && !this.currentVariant.available;
      const readyLabel = this.preorderCta || 'Add to cart';
      let label = '';
      let disabled = false;

      if (productUnavailable || variantUnavailable) {
        label = 'Sold out';
        disabled = true;
      } else if (this.requiresSize && !this.sizeSelected) {
        label = 'Select Size';
        disabled = true;
      } else {
        const price = this.currentVariant
          ? this.formatMoney(this.currentVariant.price)
          : this.dataset.currentPrice;
        label = `${readyLabel} \u2022 ${price}`;
        if (this.stickyPrice) this.stickyPrice.textContent = price;
      }

      this.mainButton.disabled = disabled;
      this.mainButton.dataset.pdpState = disabled ? (label === 'Sold out' ? 'sold-out' : 'choose-size') : 'ready';
      if (this.mainButtonText) {
        this.mainButtonText.textContent = label === 'Select Size' ? readyLabel : label;
      }

      this.stickySizeButtons.forEach((button) => {
        const input = this.sizeInputs.find((candidate) => candidate.value === button.dataset.pdpStickySize);
        button.disabled = !input || input.disabled;
        button.setAttribute('aria-pressed', String(Boolean(input?.checked)));
      });

      this.stickyButton.disabled = label === 'Sold out';
      this.stickyButton.dataset.pdpState = label === 'Select Size' ? 'choose-size' : disabled ? 'sold-out' : 'ready';
      this.stickyButton.textContent = label.startsWith(readyLabel) ? readyLabel : label;
      this.updatePaymentPlan();
      this.updatePurchaseMessages();
    }

    updatePaymentPlan() {
      if (!this.paymentPlanAmount || !this.paymentInstallmentCount || !this.currentVariant) return;
      const installmentAmount = Math.round(
        Number(this.currentVariant.price || 0) / this.paymentInstallmentCount
      );
      this.paymentPlanAmount.textContent = this.formatMoney(installmentAmount);
    }

    updatePurchaseMessages() {
      if (!this.messageGroups.length) return;

      let mode = this.messageContainer.dataset.initialMessageMode || 'regular';
      if (this.section.dataset.pdpProfile === 'swim') {
        mode = 'swim';
      } else if (this.currentVariant) {
        const price = Number(this.currentVariant.price || 0);
        const compareAtPrice = Number(this.currentVariant.compare_at_price || 0);
        mode = compareAtPrice > price ? 'sale' : 'regular';
      }

      this.messageGroups.forEach((group) => {
        group.hidden = group.dataset.pdpMessageGroup !== mode;
      });
      const benefitPolicy = mode === 'regular' ? 'regular' : 'final-sale';
      this.benefitPolicies.forEach((label) => {
        label.hidden = label.dataset.pdpBenefitPolicy !== benefitPolicy;
      });
    }

    formatMoney(cents) {
      if (window.theme?.Currency?.formatMoney && window.theme?.settings?.moneyFormat) {
        return window.theme.Currency.formatMoney(cents, window.theme.settings.moneyFormat);
      }
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    }

    reducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('tuck-pdp-purchase', TuckPdpPurchase);
})();
